import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import { useMutation } from "@apollo/client/react";
// @ts-ignore
import { load } from "@cashfreepayments/cashfree-js";
import {
  BOOK_PASS,
  CREATE_PASS_PAYMENT_ORDER,
  DOWNLOAD_PASS_INVOICE,
} from "../graphql/mutation";
import type { PassLinkedOffer, PassListItem } from "../interfaces/pass.interface";
import { getActivePassOffer } from "../utils/offerPricing";
import TruncatedTitle from "./TruncatedTitle";

type SnackbarType = "success" | "error" | "info";

interface PassPurchaseDrawerProps {
  open: boolean;
  pass: PassListItem | null;
  onClose: () => void;
  onPurchaseComplete: (message: string, type: SnackbarType) => void;
  returnPath?: string;
  bookingContext?: {
    classId: string;
    sessionDate?: string;
    className?: string;
  } | null;
  isBooked?: boolean;
}

const PASS_TYPE_LABELS: Record<string, string> = {
  "Drop-in": "Drop In",
  "Weekly": "Weekly",
  "Monthly": "Monthly",
  DROP_IN: "Drop In",
  WEEKLY_4_CLASSES: "Weekly",
  MONTHLY_4_CLASSES: "Monthly",
};

const VALIDITY_LABELS: Record<string, string> = {
  "Drop-in": "Valid for 1 Week",
  "Weekly": "Valid for 1 Week",
  "Monthly": "Valid for 1 Month",
  DROP_IN: "Valid for 1 Week",
  WEEKLY_4_CLASSES: "Valid for 1 Week",
  MONTHLY_4_CLASSES: "Valid for 1 Month",
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);

const normalizePayableAmount = (amount: number) => {
  if (amount <= 0) return 0;
  return Math.max(1, Number(amount.toFixed(2)));
};

const calculateRemainingValidity = (
  scheduledDates: string[] | undefined,
  endTime: string | null | undefined
) => {
  if (!scheduledDates || scheduledDates.length === 0 || !endTime) return null;

  try {
    const parseDate = (value: string) => {
      if (value.includes("-")) return new Date(value);
      if (value.includes(".")) {
        const parts = value.split(".");
        if (parts.length === 3) {
          const day = parts[0].padStart(2, "0");
          const month = parts[1].padStart(2, "0");
          let year = parts[2];
          if (year.length === 2) year = `20${year}`;
          return new Date(`${year}-${month}-${day}`);
        }
      }
      return new Date(value);
    };

    const latestDate = [...scheduledDates]
      .map((item) => parseDate(item))
      .sort((a, b) => b.getTime() - a.getTime())[0];

    let hours = 0;
    let minutes = 0;
    const timeMatch = endTime.match(/(\d+):(\d+)\s*(AM|PM)?/i);

    if (timeMatch) {
      hours = parseInt(timeMatch[1], 10);
      minutes = parseInt(timeMatch[2], 10);
      const modifier = timeMatch[3]?.toUpperCase();
      if (modifier === "PM" && hours < 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;
    }

    const targetDate = new Date(latestDate);
    targetDate.setHours(hours, minutes, 0, 0);

    const diffMs = targetDate.getTime() - new Date().getTime();
    if (diffMs <= 0) return "Expired";

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    let result = "";
    if (diffDays > 0) result += `${diffDays} Day${diffDays > 1 ? "s" : ""} `;
    if (diffHours > 0) result += `${diffHours} Hr${diffHours > 1 ? "s" : ""}`;
    return result.trim() || "Less than 1 hour";
  } catch (error) {
    console.error("Error calculating validity:", error);
    return null;
  }
};

const getPassSummary = (pass: PassListItem | null) => {
  if (!pass) return "";

  const linkedClassNames =
    pass.classNames && pass.classNames.length
      ? pass.classNames
      : pass.className
      ? [pass.className]
      : [];
  const summaryParts = [
    `${pass.noOfClasses} ${pass.noOfClasses === 1 ? "credit" : "credits"}`,
    linkedClassNames.length ? `for ${linkedClassNames.join(", ")}` : null,
    pass.styleType || PASS_TYPE_LABELS[pass.passType] || pass.passType,
  ].filter(Boolean);

  return summaryParts.join(" · ");
};

const PassPurchaseDrawer: React.FC<PassPurchaseDrawerProps> = ({
  open,
  pass,
  onClose,
  onPurchaseComplete,
  returnPath = "/passes",
  bookingContext = null,
  isBooked = false,
}) => {
  const [status, setStatus] = useState<{ type: SnackbarType; message: string } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const [bookPass] = useMutation(BOOK_PASS);
  const [createPassPaymentOrder] = useMutation(CREATE_PASS_PAYMENT_ORDER);
  const [downloadPassInvoice] = useMutation(DOWNLOAD_PASS_INVOICE);

  useEffect(() => {
    if (open) {
      setStatus(null);
      setProcessing(false);
      setPurchaseId(null);
      setDownloadingInvoice(false);
    }
  }, [open, pass?.id]);

  const activeOffer = useMemo(
    () => getActivePassOffer(pass?.offers as PassLinkedOffer[] | undefined),
    [pass]
  );

  const discountedPrice = useMemo(() => {
    if (!pass) return 0;
    if (!activeOffer) return normalizePayableAmount(pass.passCost || 0);
    return normalizePayableAmount(
      pass.passCost - (pass.passCost * activeOffer.discount) / 100
    );
  }, [activeOffer, pass]);

  const dynamicValidity = useMemo(
    () => calculateRemainingValidity(pass?.classScheduledDates, pass?.classEndTime),
    [pass]
  );

  const validityLabel = pass
    ? VALIDITY_LABELS[pass.passType] ||
      (dynamicValidity ? `Valid for ${dynamicValidity}` : "Valid for 1 Month")
    : "";

  const handlePayment = async () => {
    if (!pass) return;
    setProcessing(true);
    setStatus({ type: "info", message: "Preparing your secure checkout..." });

    try {
      if (discountedPrice <= 0) {
        const { data } = await bookPass({
          variables: {
            passId: pass.id,
            sessionDate: bookingContext?.sessionDate || null,
          },
        });
        if (data?.bookPass?.success) {
          const message = data.bookPass.message || "Pass booked successfully!";
          const newPurchaseId = data.bookPass.data?.id;
          setPurchaseId(newPurchaseId);
          setStatus({ type: "success", message });
          onPurchaseComplete(message, "success");
          return;
        }

        const errorMessage = data?.bookPass?.message || "Failed to book pass";
        setStatus({ type: "error", message: errorMessage });
        onPurchaseComplete(errorMessage, "error");
        return;
      }

      const { data: orderRes } = await createPassPaymentOrder({
        variables: {
          passId: pass.id,
          finalPrice: discountedPrice,
          promoCode: activeOffer?.offerCode,
          sessionDate: bookingContext?.sessionDate || null,
          returnUrl: `${window.location.origin}/payment/callback?source=pass&redirect=${encodeURIComponent(returnPath)}&order_id={order_id}`,
        },
      });

      if (!orderRes?.createPassPaymentOrder?.success) {
        const errorMessage =
          orderRes?.createPassPaymentOrder?.message || "Failed to initialize payment";
        setStatus({ type: "error", message: errorMessage });
        onPurchaseComplete(errorMessage, "error");
        return;
      }

      const { paymentSessionId, cfMode } = orderRes.createPassPaymentOrder;
      const cashfree = await load({ mode: cfMode || "sandbox" });

      if (!cashfree) {
        throw new Error("Cashfree SDK failed to load");
      }

      setStatus({ type: "info", message: "Redirecting you to the payment page..." });
      await cashfree.checkout({
        paymentSessionId,
        redirectTarget: "_self",
      });
    } catch {
      const errorMessage = "An error occurred. Please try again.";
      setStatus({ type: "error", message: errorMessage });
      onPurchaseComplete(errorMessage, "error");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!purchaseId) return;
    try {
      setDownloadingInvoice(true);
      const { data } = await downloadPassInvoice({
        variables: { purchaseId },
      });

      const result = data?.downloadPassInvoice;
      if (!result?.success || !result.contentBase64) {
        throw new Error(result?.message || "Failed to download invoice");
      }

      const byteCharacters = window.atob(result.contentBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i += 1) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: result.mimeType || "application/pdf",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = result.filename || `Invoice_${purchaseId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Error downloading invoice:", err);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (status?.type === "success") {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 440 },
            background: "#FFFFFF",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            px: 4,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#F0FBE8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 48, color: "#6BC242" }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#111827", mb: 1 }}>
            Success!
          </Typography>
          <Typography sx={{ color: "#6B7280", mb: 4, px: 2 }}>
            {status.message}
          </Typography>

          <Stack spacing={2} sx={{ width: "100%" }}>
            {purchaseId && (
              <Button
                fullWidth
                variant="contained"
                onClick={handleDownloadInvoice}
                disabled={downloadingInvoice}
                startIcon={downloadingInvoice ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                sx={{
                  backgroundColor: "#6BC242",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 15,
                  borderRadius: "14px",
                  py: 1.6,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#62B63C",
                  },
                }}
              >
                {downloadingInvoice ? "Generating Invoice..." : "Download Invoice"}
              </Button>
            )}
            
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: "#E5E7EB",
                color: "#6B7280",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 15,
                borderRadius: "14px",
                py: 1.6,
                "&:hover": {
                  borderColor: "#D1D5DB",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              Close
            </Button>
          </Stack>
        </Box>
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        if (!processing) onClose();
      }}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 440 },
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #FCFDFB 52%, #F5F8F3 100%)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderBottom: "1px solid #EDF2EA",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
            {isBooked ? "Pass details" : "Complete your purchase"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.25 }}>
            {isBooked 
              ? "You have already purchased this pass." 
              : "Review your pass and continue with secure payment."}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={processing}
          size="small"
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "10px",
            color: "#6B7280",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 3 },
          pt: 1,
          pb: 2.5,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {pass && (
          <Stack spacing={2.25}>
            <Box
              sx={{
                borderRadius: "24px",
                p: { xs: 2, sm: 2.5 },
                background:
                  "linear-gradient(135deg, #0F172A 0%, #1F7A3F 48%, #A4D65E 100%)",
                color: "#fff",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: -24,
                  right: -28,
                  width: 110,
                  height: 110,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.12)",
                }}
              />
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255,255,255,0.16)",
                    mb: 2,
                  }}
                >
                  <CardMembershipOutlinedIcon sx={{ fontSize: 28 }} />
                </Box>
                <TruncatedTitle
                  text={pass.passName}
                  sx={{
                    fontSize: 24,
                    fontWeight: 800,
                    lineHeight: 1.15,
                    color: "#fff",
                  }}
                />
                <Typography sx={{ fontSize: 14, opacity: 0.9, mt: 0.75, maxWidth: 360 }}>
                  {getPassSummary(pass)}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderRadius: "999px",
                      backgroundColor: "rgba(255,255,255,0.14)",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {validityLabel}
                  </Box>

                </Stack>
              </Box>
            </Box>

            <Box
              sx={{
                borderRadius: "20px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E8EEE3",
                boxShadow: "0px 12px 30px rgba(17, 24, 39, 0.06)",
                overflow: "hidden",
              }}
            >
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#6B7280", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  Pass details
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 1.75 }}>
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Pass type</Typography>
                    <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700, textAlign: "right" }}>
                      {PASS_TYPE_LABELS[pass.passType] || pass.passType}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Credits</Typography>
                    <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700 }}>
                      {pass.noOfClasses}
                    </Typography>
                  </Box>
                  {(() => {
                    const linkedClasses =
                      pass.classNames && pass.classNames.length
                        ? pass.classNames
                        : pass.className
                        ? [pass.className]
                        : [];
                    if (linkedClasses.length === 0) return null;
                    return (
                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
                          {linkedClasses.length > 1 ? "Classes" : "Class"}
                        </Typography>
                        <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700, textAlign: "right" }}>
                          {linkedClasses.join(", ")}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ px: 2.5, py: 2.25, backgroundColor: "#FBFCFA" }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#6B7280", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  {isBooked ? "Purchase summary" : "Payment details"}
                </Typography>

                <Stack spacing={1.2} sx={{ mt: 1.75 }}>
                  {!isBooked && (
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Original price</Typography>
                      <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700 }}>
                        {pass.passCost === 0 ? "Free" : formatCurrency(pass.passCost || 0)}
                      </Typography>
                    </Box>
                  )}

                  {activeOffer && !isBooked && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          backgroundColor: "#F0FBE8",
                          border: "1px solid #C6ECA8",
                          borderRadius: "999px",
                          px: 1.25,
                          py: 0.5,
                        }}
                      >
                        <LocalOfferOutlinedIcon sx={{ fontSize: 14, color: "#5AA839" }} />
                        <Typography sx={{ color: "#5AA839", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                          {activeOffer.discount}% off
                        </Typography>
                      </Box>
                      <Typography sx={{ color: "#5AA839", fontSize: 14, fontWeight: 800 }}>
                        -{formatCurrency((pass.passCost * activeOffer.discount) / 100)}
                      </Typography>
                    </Box>
                  )}

                  {!isBooked && <Divider sx={{ borderColor: "#E5E7EB" }} />}

                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={2}>
                    <Box>
                      <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
                        {isBooked ? "Purchase status" : "Amount payable"}
                      </Typography>
                      <Typography sx={{ color: isBooked ? "#6BC242" : "#111827", fontSize: isBooked ? 20 : 28, fontWeight: 900, lineHeight: 1.1, mt: 0.4 }}>
                        {isBooked ? "Booked" : (discountedPrice === 0 ? "Free" : formatCurrency(discountedPrice))}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>

          </Stack>
        )}
      </Box>

      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: "1px solid #E8EEE3",
          backgroundColor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={handlePayment}
          disabled={!pass || processing || isBooked}
          endIcon={!processing && !isBooked ? <ArrowForwardIcon sx={{ fontSize: 24 }} /> : undefined}
          sx={{
            backgroundColor: isBooked ? "#F3F4F6" : "#6BC242",
            color: isBooked ? "#9CA3AF" : "#fff",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 15,
            borderRadius: "14px",
            py: 1.6,
            minHeight: 56,
            boxShadow: "none",
            letterSpacing: "0.01em",
            "&:hover": {
              backgroundColor: isBooked ? "#F3F4F6" : "#62B63C",
            },
            "&:disabled": {
              color: isBooked ? "#9CA3AF" : "#fff",
              background: isBooked ? "#F3F4F6" : "#B7D9A8",
            },
            "& .MuiButton-endIcon": {
              ml: 1,
            },
          }}
        >
          {processing ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} sx={{ color: "#fff" }} />
              Processing...
            </Box>
          ) : isBooked ? (
            "Already booked"
          ) : discountedPrice <= 0 ? (
            "Confirm booking"
          ) : (
            `Pay ${formatCurrency(discountedPrice)}`
          )}
        </Button>
      </Box>
    </Drawer>
  );
};

export default PassPurchaseDrawer;
